import { getToken } from "../storage/authStorage";
import { API_BASE_URL } from "../../../config/api";

class APIError extends Error {
  public status: number;
  public data: any;

  constructor(message: string, status: number, data: any = null) {
    super(message);
    this.name = "APIError";
    this.status = status;
    this.data = data;
  }
}

const HTTP_STATUS = {
  UNAUTHORIZED: 401,
  INTERNAL_SERVER_ERROR: 500,
};

export interface ProposedDecision {
  line_id: string;
  reason: string;
  source_layer: "override" | "knn" | "clf" | "llm";
  confidence: number;
  suggested_gl: string;
  account_name: string;
}

export interface ProposedDecisionsResponse {
  success: boolean;
  data: ProposedDecision[];
  message: string;
}

/**
 * Fetch proposed decisions by job ID
 *
 * @param jobId - The job ID (invoice_id) to fetch proposed decisions for
 * @returns Promise<ProposedDecision[]> - Array of proposed decisions ordered by line_id
 * @throws APIError - If the request fails or authentication is invalid
 */
export const fetchProposedDecisions = async (
  jobId: string
): Promise<ProposedDecision[]> => {
  const token = getToken();

  if (!token) {
    throw new APIError(
      "No authentication token found",
      HTTP_STATUS.UNAUTHORIZED
    );
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/api/proposed-decisions/${jobId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new APIError(
        errorData.message || `HTTP error! status: ${response.status}`,
        response.status,
        errorData
      );
    }

    const data: ProposedDecisionsResponse = await response.json();
    return data.data;
  } catch (error) {
    if (error instanceof APIError) {
      throw error;
    }
    throw new APIError(
      `Network error: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      HTTP_STATUS.INTERNAL_SERVER_ERROR
    );
  }
};
